export type InquiryQueue = 'needs_reply' | 'replied' | 'offer_pending' | 'closed';

export const INQUIRY_QUEUE_FILTERS = ['needs_reply', 'replied', 'offer_pending', 'closed', 'all'] as const;

export type InquiryQueueFilter = (typeof INQUIRY_QUEUE_FILTERS)[number];

export const INQUIRY_SCOPES = ['anyone', 'mine', 'unassigned'] as const;

export type InquiryScope = (typeof INQUIRY_SCOPES)[number];

export type InquiryAuthor = 'BUYER' | 'SELLER' | 'ARBITER';

export type InquiryMessage = {
  postedBy: InquiryAuthor;
  at: string;
  text: string;
  documents: { name: string; index: number; url: string }[];
  sentBy: string | null;
  sentAttachment?: { name: string; mimeType: string; url: string };
  offer?: { amount: number; currency: string };
  attachmentName?: string;
  pending?: boolean;
  held?: true;
  failed?: string;
};

export type InquiryListItem = {
  disputeId: number;
  paypalDisputeKey: string;
  companyName: string;
  brandName: string | null;
  buyerName: string;
  amount: number;
  currency: string;
  reason: string | null;
  status: string | null;
  queue: InquiryQueue;
  proofRequested: boolean;
  lastMessageBy: InquiryAuthor | null;
  lastMessageAt: string | null;
  lastMessagePreview: string;
  messageCount: number;
  waitingSince: string | null;
  sellerDueAt: string | null;
  assignedUserId: number | null;
  assignedFirstName: string | null;
  evidenceSentAt: string | null;
};

export type InquiryCounts = {
  needs_reply: number;
  replied: number;
  offer_pending: number;
  mine: number;
  unassigned: number;
};

export type InquiryFacts = {
  orderName: string | null;
  source: 'shopify' | 'checkout_champ' | null;
  placedAt: string | null;
  orderType: 'initial' | 'renewal' | 'unknown';
  originalOrderAt: string | null;
  renewedAt: string | null;
  subscriptionCancelledAt: string | null;
  shippedAt: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  refundedTotal: number | null;
  financialStatus: string | null;
  error: string | null;
};

export const TEMPLATE_LANGUAGES = ['en', 'de', 'fr', 'es'] as const;

export type TemplateLanguage = (typeof TEMPLATE_LANGUAGES)[number];

export type TemplateGroup = 'explain' | 'offer';

export type InquiryTemplateItem = {
  key: string;
  label: string;
  group: TemplateGroup;
  text: string;
};

export type InquiryTimelineItem = {
  kind: 'opened' | 'buyer_message' | 'seller_message' | 'offer' | 'status' | 'due' | 'failed' | 'proof_requested' | 'evidence_sent';
  at: string;
  label: string;
};

export type InquiryPayment = {
  paidAt: string | null;
  amount: number | null;
  currency: string | null;
  items: { name: string; quantity: string }[];
  buyerEmail: string | null;
  transactionId: string | null;
  protectionEligible: boolean | null;
  url: string | null;
};

export type InquiryDetail = {
  dispute: {
    disputeId: number;
    paypalDisputeKey: string;
    companyName: string;
    brandName: string | null;
    buyerName: string;
    amount: number;
    currency: string;
    reason: string | null;
    status: string | null;
    outcomeCode: string | null;
    stage: string | null;
    openedAt: string | null;
    sellerDueAt: string | null;
    assignedUserId: number | null;
    assignedFirstName: string | null;
    paypalUrl: string;
  };
  queue: InquiryQueue;
  proofRequested: boolean;
  proof: { askedAt: string[]; evidenceSentAt: string | null; evidenceSentBy: string | null } | null;
  messages: InquiryMessage[];
  payment: InquiryPayment | null;
  facts: InquiryFacts;
  factsAt: string | null;
  factsRefreshing: boolean;
  allowed: { message: boolean; offer: boolean; accept: boolean };
  offerTypes: string[];
  acceptTypes: string[];
  timeline: InquiryTimelineItem[];
  syncedAt: string | null;
  refreshing: boolean;
  version: string;
};

export type InquiryListResponse = {
  inquiries: InquiryListItem[];
  counts: InquiryCounts;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
};
